import {Injectable} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {Inject} from '@angular/core';
import {TranslateService as BaseTranslateService} from '@ngx-translate/core';
import {UserService} from "./user.service";
import {SettingsService} from "./settings.service";
import {Device, GetLanguageCodeResult} from "@capacitor/device";
import {Capacitor} from '@capacitor/core';
import {ContactListType} from "../models/contact";
import {firstValueFrom} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class TranslateService {

    private locale = 'fr';
    private authorizedLocales = ['fr', 'en'];
    private initialized = false;

    constructor(
        private readonly translate: BaseTranslateService,
        private readonly settingsService: SettingsService,
        @Inject(DOCUMENT) private readonly document: Document
    ) {
        this.translate.setDefaultLang('fr');
    }

    private setDefaultLocale(): Promise<void> {
        this.locale = 'fr';
        this.updateHtmlLang('fr');
        return this.useLocale('fr');
    }

    private updateHtmlLang(locale: string): void {
        this.document.documentElement.lang = locale;
    }

    private doChangeLocale(locale: string): Promise<void> {
        // If the locale is the same and the service is already initialized, do nothing
        if (locale === this.locale && this.initialized) {
            return Promise.resolve();
        }

        // If the locale is not authorized, set the default locale
        if (!this.authorizedLocales.includes(locale)) {
            return this.setDefaultLocale();
        }

        this.locale = locale;
        this.updateHtmlLang(locale);
        this.settingsService.setSettings('language', this.locale);
        return this.useLocale(locale);
    }

    private useLocale(locale: string): Promise<void> {
        return firstValueFrom(this.translate.use(locale)).then(
            () => {
                this.initialized = true;
            },
            () => {
                this.initialized = true;
            },
        );
    }

    /** Resolves with the translated string once the language file has loaded. */
    getWhenReady(key: string): Promise<string> {
        return firstValueFrom(this.translate.get(key)).then(value => String(value));
    }

    async init() {
        // Get the saved locale
        const savedLocale = this.settingsService.getSettings('language');
        if (savedLocale) {
            return this.doChangeLocale(savedLocale);
        }

        // Get locale from the device
        if (Capacitor.isNativePlatform()) {
             const languageResult = await Device.getLanguageCode();
             if (languageResult?.value) {
                 return this.doChangeLocale(languageResult.value.toLowerCase());
             }
        }

        // Get locale from the browser
        const locale = this.translate.getBrowserLang();
        if (locale) {
            return this.doChangeLocale(locale);
        }

        // Set the default locale
        return this.setDefaultLocale();
    }

    getLocale(): string {
        return this.locale;
    }

    get(key: string|string[], params?: any, locale?: string): any {
        if (locale) {
            return this.translate.instant(key, params);
        }
        return this.translate.instant(key, params);
    }

    changeLocale(locale: string) {
        return this.doChangeLocale(locale);
    }

    getContactListName(list: ContactListType): string {
        return this.get(`contacts.${list}`);
    }
}
